import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import LinkedInConnect from "../components/LinkedInConnect";
import ActivityFeed from "../components/ActivityFeed";
import OnboardingChecklist from "../components/OnboardingChecklist";

const statCards = [
  {
    key: "total_segments",
    label: "Segments",
    color: "text-rouge",
    bg: "bg-rouge-soft",
  },
  {
    key: "active_segments",
    label: "Segments actifs",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  {
    key: "messages_today",
    label: "Messages aujourd'hui",
    color: "text-blue-700",
    bg: "bg-blue-50",
  },
  {
    key: "messages_total",
    label: "Messages total",
    color: "text-violet-700",
    bg: "bg-violet-50",
  },
];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // null = pas encore chargé → évite le flash checklist « non connecté »
  const [linkedInConnected, setLinkedInConnected] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      api
        .get("/api/dashboard/stats")
        .catch(() => ({
          total_segments: 0,
          active_segments: 0,
          messages_today: 0,
          messages_total: 0,
        })),
      api
        .get("/api/linkedin/status")
        .then((data) => data?.connected === true)
        .catch(() => false),
      api
        .get("/api/auth/me")
        .then((data) => (data?.plan || "freemium").toLowerCase())
        .catch(() => "freemium"),
    ]).then(([statsData, connected, userPlan]) => {
      if (cancelled) return;
      setStats(statsData);
      setLinkedInConnected(connected);
      setPlan(userPlan);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const ready = !loading && stats && linkedInConnected !== null;
  const showOnboarding =
    ready && (!linkedInConnected || (stats.total_segments ?? 0) === 0);
  // Évite le double CTA « premier segment » quand la checklist est déjà là
  const showEmptySegmentsCta =
    ready && !showOnboarding && (stats.total_segments ?? 0) === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-ink tracking-tight">Tableau de bord</h1>
        <p className="text-sm text-mute mt-1">
          Vue d'ensemble de votre activité LinkedIn
        </p>
      </div>

      {showOnboarding && (
        <OnboardingChecklist
          linkedInConnected={linkedInConnected}
          segmentsCount={stats?.total_segments ?? 0}
          activeSegmentsCount={stats?.active_segments ?? 0}
          plan={plan || "freemium"}
        />
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.key} className="card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center ${card.color}`}>
                <span className="font-display font-semibold text-lg">
                  {loading ? "—" : (stats?.[card.key] ?? 0)}
                </span>
              </div>
            </div>
            <p className="type-label-accent">{card.label}</p>
            <div className="text-2xl font-display font-bold text-ink mt-1">
              {loading ? (
                <div className="h-7 w-12 bg-ink/5 rounded animate-pulse" />
              ) : (
                stats?.[card.key] ?? 0
              )}
            </div>
          </div>
        ))}
      </div>

      {/* LinkedIn connection */}
      <LinkedInConnect />

      {showEmptySegmentsCta && (
        <div className="card p-8 text-center">
          <svg
            className="w-16 h-16 text-ink/10 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <h3 className="text-lg font-display font-semibold text-ink mb-2 tracking-tight">
            Créez votre premier segment
          </h3>
          <p className="text-sm text-mute mb-6 max-w-md mx-auto">
            Définissez une audience cible et des instructions pour l'IA.
            Elle enverra automatiquement des messages personnalisés
            chaque jour.
          </p>
          <Link to="/segments/new" className="btn-primary inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nouveau segment
          </Link>
        </div>
      )}

      {/* Activity feed */}
      <ActivityFeed />
    </div>
  );
}
